import { type RefObject, useEffect, useRef } from "react";

const BAR_WIDTH = 3;
const BAR_GAP = 2;
const BAR_RADIUS = 2;
const BASE_BAR_HEIGHT = 4;
const FADE_EDGES = true;
const FADE_WIDTH = 24;
const SENSITIVITY = 1.5;
const UPDATE_RATE = 30;

export type LiveWaveformProps = {
  analyserRef: RefObject<AnalyserNode | null>;
};

export function LiveWaveform({ analyserRef }: LiveWaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastUpdateRef = useRef<number>(0);
  const staticBarsRef = useRef<number[]>([]);
  const needsRedrawRef = useRef(true);
  const gradientCacheRef = useRef<CanvasGradient | null>(null);
  const lastWidthRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;

    if (!canvas || !container) return;
    const resizeObserver = new ResizeObserver(() => {
      const rect = container.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;

      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;

      const ctx = canvas.getContext("2d");

      if (ctx) {
        ctx.scale(dpr, dpr);
      }

      gradientCacheRef.current = null;
      needsRedrawRef.current = true;
    });

    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, []);

  // Animation loop
  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) return;

    const ctx = canvas.getContext("2d");

    if (!ctx) return;

    let rafId: number;

    const animate = (currentTime: number) => {
      const rect = canvas.getBoundingClientRect();

      if (currentTime - lastUpdateRef.current > UPDATE_RATE) {
        lastUpdateRef.current = currentTime;

        if (analyserRef?.current) {
          const dataArray = new Uint8Array(
            analyserRef.current.frequencyBinCount,
          );

          analyserRef.current.getByteFrequencyData(dataArray);

          const startFreq = Math.floor(dataArray.length * 0.05);
          const endFreq = Math.floor(dataArray.length * 0.4);

          const relevantData = dataArray.slice(startFreq, endFreq);

          const barCount = Math.floor(rect.width / (BAR_WIDTH + BAR_GAP));
          const halfCount = Math.floor(barCount / 2);

          const newBars: number[] = [];

          for (let i = halfCount - 1; i >= 0; i--) {
            const dataIndex = Math.floor((i / halfCount) * relevantData.length);

            const value = Math.min(
              1,
              (relevantData[dataIndex] / 255) * SENSITIVITY,
            );
            newBars.push(Math.max(0.05, value));
          }

          for (let i = 0; i < halfCount; i++) {
            const dataIndex = Math.floor((i / halfCount) * relevantData.length);

            const value = Math.min(
              1,
              (relevantData[dataIndex] / 255) * SENSITIVITY,
            );

            newBars.push(Math.max(0.05, value));
          }

          staticBarsRef.current = newBars;
          needsRedrawRef.current = true;
        }
      }

      needsRedrawRef.current = true;

      ctx.clearRect(0, 0, rect.width, rect.height);

      const computedBarColor = getComputedStyle(canvas).color;

      const step = BAR_WIDTH + BAR_GAP;
      const barCount = Math.floor(rect.width / step);

      const dataToRender = staticBarsRef.current;

      for (let i = 0; i < barCount && i < dataToRender.length; i++) {
        const value = dataToRender[i] || 0.1;
        const x = i * step;
        const h = Math.max(BASE_BAR_HEIGHT, value * rect.height * 0.8);
        const y = rect.height / 2 - h / 2;

        ctx.fillStyle = computedBarColor;
        ctx.globalAlpha = 0.4 + value * 0.6;

        if (BAR_RADIUS > 0) {
          ctx.beginPath();
          ctx.roundRect(x, y, BAR_WIDTH, h, BAR_RADIUS);

          ctx.fill();
        } else {
          ctx.fillRect(x, y, BAR_WIDTH, h);
        }
      }

      if (FADE_EDGES && FADE_WIDTH > 0 && rect.width > 0) {
        if (!gradientCacheRef.current || lastWidthRef.current !== rect.width) {
          const gradient = ctx.createLinearGradient(0, 0, rect.width, 0);
          const fadePercent = Math.min(0.3, FADE_WIDTH / rect.width);

          gradient.addColorStop(0, "rgba(255,255,255,1)");
          gradient.addColorStop(fadePercent, "rgba(255,255,255,0)");
          gradient.addColorStop(1 - fadePercent, "rgba(255,255,255,0)");
          gradient.addColorStop(1, "rgba(255,255,255,1)");

          gradientCacheRef.current = gradient;
          lastWidthRef.current = rect.width;
        }
        ctx.globalCompositeOperation = "destination-out";
        ctx.fillStyle = gradientCacheRef.current;

        ctx.fillRect(0, 0, rect.width, rect.height);
        ctx.globalCompositeOperation = "source-over";
      }
      ctx.globalAlpha = 1;
      rafId = requestAnimationFrame(animate);
    };

    rafId = requestAnimationFrame(animate);

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  return (
    <div
      className="relative h-[27px] flex-1 text-white"
      ref={containerRef}
      aria-label="Live audio waveform"
      role="img"
    >
      <canvas className="block h-full w-full" ref={canvasRef} />
    </div>
  );
}
